import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, combineLatestWith, defer, map, Observable, of, startWith, switchMap, tap } from 'rxjs';
import { BotsService, CorpsService, HierarchyElementsService } from 'src/app/services/firestore';
import { ClientEnvironmentService } from 'src/app/services/client-environment.service';
import { ToastrService } from 'ngx-toastr';
import { DumbledoreService } from 'src/app/services/dumbledore.service';
import { environment } from 'src/environments/environment';

interface KnowledgeBankViewModel {
  isLoading: boolean;
  dealerId: string | null;
}

const loading: KnowledgeBankViewModel = { isLoading: true, dealerId: null };
const unavailable: KnowledgeBankViewModel = { isLoading: false, dealerId: null };

@Component({
  selector: 'app-bot-knowledge-bank',
  templateUrl: './bot-knowledge-bank.component.html',
  styleUrls: ['./bot-knowledge-bank.component.scss'],
})
export class BotKnowledgeBankComponent {
  API: string = environment.knowledgeBankAPIKey;

  constructor(
    private activatedRoute: ActivatedRoute,
    private corpsService: CorpsService,
    private hierarchyElementsService: HierarchyElementsService,
    private botsService: BotsService,
    private clientEnvironmentService: ClientEnvironmentService,
    private toaster: ToastrService,
    private dumbledoreService: DumbledoreService,
    private router: Router,
  ) {}

  readonly vm$: Observable<KnowledgeBankViewModel> = this.activatedRoute.parent!.paramMap.pipe(
    combineLatestWith(this.clientEnvironmentService.items$),
    switchMap(([params, envs]) => {
      const corpId = params.get('corp');
      const hierarchyElementSystemName = params.get('hierarchyElementSystemName');
      const botCode = params.get('bot');
      if (!corpId || !hierarchyElementSystemName || !botCode || !envs) {
        return of(loading);
      }

      return this.corpsService.getCorpById(corpId).pipe(
        combineLatestWith(
          this.hierarchyElementsService.getHierarchyElement(`${corpId}-${hierarchyElementSystemName}`),
          this.botsService.getBotBy(corpId, hierarchyElementSystemName, botCode, envs),
        ),
        switchMap(([corp, hierarchyElement, bot]) => {
          if (!corp || !hierarchyElement || !bot) {
            return of(unavailable);
          }

          return defer(() => this.dumbledoreService.getBotConfigurationByDealershipCode(`${corp.id}-${hierarchyElementSystemName}`)).pipe(
            tap(botConfig => {
              if (!botConfig?.knowledgeBase == true) {
                this.toaster.error('You do not have permission to access this page');
                this.router.navigate(['/portal/dashboard']);
              }
            }),
            map(botConfig => ({ isLoading: false, dealerId: botConfig?.salesforceId ?? null })),
            catchError(error => {
              if (error?.status !== 404) {
                this.toaster.error(error);
              }
              return of(unavailable);
            }),
          );
        }),
        catchError(error => {
          this.toaster.error(error);
          return of(unavailable);
        }),
        startWith(loading),
      );
    }),
    startWith(loading),
  );
}
